import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { getOwnerBusinesses } from "../services/onboardingApi";
import { 
  Building2, 
  Utensils, 
  Store, 
  Mail, 
  Phone, 
  ShieldCheck, 
  Calendar, 
  ArrowRight,
  Sparkles
} from "lucide-react";
import { Link } from "react-router-dom";

const Profile = () => {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBusinesses = async () => {
      if (!user?.ownerId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getOwnerBusinesses(user.ownerId);
        if (data && data.success) {
          setBusinesses(data.businesses || []);
        } else {
          setError(data.message || "Failed to load outlets.");
        }
      } catch (err) {
        console.error("Error fetching outlets:", err);
        setError("Unable to connect to the server. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBusinesses();
  }, [user]);

  // Count businesses by type
  const counts = businesses.reduce(
    (acc, cur) => {
      const type = (cur.businessType || "").toUpperCase();
      if (type === "HOTEL") acc.hotels += 1;
      else if (type === "RESTAURANT") acc.restaurants += 1;
      else if (type === "SHOP" || type === "SHOPKEEPER") acc.shops += 1;
      return acc;
    },
    { hotels: 0, restaurants: 0, shops: 0 }
  );

  const getStatusBadgeClass = (status) => {
    const s = (status || "").toUpperCase();
    if (s === "ACTIVE" || s === "APPROVED") {
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20";
    }
    if (s === "PENDING") {
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20";
    }
    return "bg-gray-500/10 text-gray-600 dark:text-gray-400 border border-gray-500/20";
  };

  const getBusinessIcon = (type) => {
    const t = (type || "").toUpperCase();
    if (t === "HOTEL") return <Building2 className="w-6 h-6 text-blue-500" />;
    if (t === "RESTAURANT") return <Utensils className="w-6 h-6 text-amber-500" />;
    return <Store className="w-6 h-6 text-emerald-500" />;
  };

  const getBusinessColorClass = (type) => {
    const t = (type || "").toUpperCase();
    if (t === "HOTEL") return "border-blue-500/20 hover:border-blue-500 bg-blue-50/10";
    if (t === "RESTAURANT") return "border-amber-500/20 hover:border-amber-500 bg-amber-50/10";
    return "border-emerald-500/20 hover:border-emerald-500 bg-emerald-50/10";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* Profile / Owner Card */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-500/10 to-indigo-500/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-lg shadow-blue-500/20">
                {(user?.ownerName || user?.email || "?")[0].toUpperCase()}
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: "Montserrat, sans-serif" }}>
                    {user?.ownerName || "Owner Profile"}
                  </h1>
                  {user?.isVerified && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                      <ShieldCheck className="w-3.5 h-3.5" /> Verified
                    </span>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4 text-gray-400" /> {user?.email}
                  </span>
                  {user?.contactPhone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4 text-gray-400" /> {user.contactPhone}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/onboarding?addOutlet=true"
                className="flex items-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-md hover:shadow-lg transition-all hover:scale-[1.02]"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                <Sparkles className="w-4 h-4" /> Add Outlet
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Hotels</p>
              <h3 className="text-3xl font-extrabold text-gray-900 mt-1">{counts.hotels}</h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Restaurants</p>
              <h3 className="text-3xl font-extrabold text-gray-900 mt-1">{counts.restaurants}</h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
              <Utensils className="w-6 h-6 text-amber-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Shops</p>
              <h3 className="text-3xl font-extrabold text-gray-900 mt-1">{counts.shops}</h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Store className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>

        {/* Outlets Listing Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "Montserrat, sans-serif" }}>
              My Registered Outlets ({businesses.length})
            </h2>
          </div>

          {loading ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm flex flex-col items-center justify-center gap-3">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-500 text-sm font-medium">Loading your outlets...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-600 rounded-2xl p-6 text-center border border-red-100 shadow-sm">
              <p className="font-semibold">{error}</p>
            </div>
          ) : businesses.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm space-y-4">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto">
                <Store className="w-8 h-8 text-gray-400" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: "Montserrat, sans-serif" }}>No Outlets Registered</h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto">You haven't added any businesses yet. Complete onboarding to configure your outlets.</p>
              </div>
              <Link
                to="/onboarding"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md transition-colors"
              >
                Start Onboarding <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {businesses.map((biz) => {
                const type = (biz.businessType || "").toUpperCase();
                const displayType = type === "SHOPKEEPER" ? "SHOP" : type;
                
                return (
                  <div 
                    key={biz.onboardingId || biz.businessId}
                    className={`bg-white rounded-2xl p-6 shadow-sm border transition-all duration-300 hover:shadow-md ${getBusinessColorClass(type)}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-white rounded-xl shadow-sm border border-gray-100">
                          {getBusinessIcon(type)}
                        </div>
                        <div>
                          <span className="text-[10px] font-bold tracking-wider uppercase bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            {displayType}
                          </span>
                          <h3 className="text-lg font-bold text-gray-900 mt-1 line-clamp-1">
                            {biz.businessName}
                          </h3>
                        </div>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${getStatusBadgeClass(biz.status)}`}>
                        {biz.status || "PENDING"}
                      </span>
                    </div>

                    <div className="space-y-2.5 text-sm text-gray-600 border-t border-gray-100/50 pt-4">
                      {biz.businessId && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-400">Outlet Code:</span>
                          <span className="font-mono font-bold text-gray-800">
                            {/* SHOP: show real shop_unique_code from vendor_shops */}
                            {(type === "SHOP" || type === "SHOPKEEPER")
                              ? (biz.shopUniqueCode || `SHP-${biz.businessId}`)
                              : type === "HOTEL"
                              ? `HTL-${biz.businessId}`
                              : type === "RESTAURANT"
                              ? `RST-${biz.businessId}`
                              : `BIZ-${biz.businessId}`
                            }
                          </span>
                        </div>
                      )}
                      
                      {biz.createdAt && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-400">Registered On:</span>
                          <span className="flex items-center gap-1 font-medium text-gray-800">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            {new Date(biz.createdAt).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                              year: "numeric"
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Profile;
